import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Leaf, Heart, Sparkles, FileText, Target, User, LogIn } from 'lucide-react';

// FBM Logo URL
const FBM_LOGO_URL = "https://customer-assets.emergentagent.com/job_plan-generator-6/artifacts/6or5u7no_Fit%20Bharat%20Mission%20Logo.png";

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Heart className="w-6 h-6 md:w-8 md:h-8" />,
      title: "Personalized Assessment",
      description: "Comprehensive health questionnaire tailored to understand your unique needs"
    },
    {
      icon: <Leaf className="w-6 h-6 md:w-8 md:h-8" />,
      title: "Natural Approach",
      description: "Diet plans based on holistic health principles and natural Indian ingredients"
    },
    {
      icon: <FileText className="w-6 h-6 md:w-8 md:h-8" />,
      title: "AI-Generated Plans",
      description: "Get your customized 3-month diet and lifestyle plan powered by AI"
    },
    {
      icon: <Target className="w-6 h-6 md:w-8 md:h-8" />,
      title: "Goal-Oriented",
      description: "Plans designed to help you achieve your specific health and wellness goals"
    }
  ];

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#F9F8F6] relative overflow-hidden">
      {/* Grain overlay */}
      <div className="grain-overlay" />
      
      {/* Navigation - Mobile optimized with sticky header */}
      <nav className="sticky top-0 left-0 right-0 z-50 px-4 md:px-6 py-3 md:py-4 bg-[#F9F8F6]/80 backdrop-blur-md border-b border-[#E5E0D1]/50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2 md:gap-3"
          >
            <img 
              src={FBM_LOGO_URL} 
              alt="Fit Bharat Mission" 
              className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border-2 border-[#D2E823]"
            />
            <span className="font-serif text-lg md:text-2xl font-bold text-[#1A4D2E]">Fit Bharat Mission</span>
          </motion.div>
          
          {/* Sign In / Sign Up Buttons */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2 md:gap-3"
          >
            <Link to="/user/login">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-[#1A4D2E] hover:bg-[#1A4D2E]/10 hidden sm:flex items-center gap-1.5"
                data-testid="nav-signin-btn"
              >
                <LogIn size={16} />
                <span>Sign In</span>
              </Button>
              {/* Mobile: Icon only */}
              <Button 
                variant="ghost" 
                size="icon"
                className="text-[#1A4D2E] hover:bg-[#1A4D2E]/10 sm:hidden"
                data-testid="nav-signin-btn-mobile"
              >
                <LogIn size={18} />
              </Button>
            </Link>
            <Link to="/questionnaire">
              <Button 
                size="sm"
                className="bg-[#1A4D2E] hover:bg-[#2D6A4F] text-white hidden sm:flex items-center gap-1.5"
                data-testid="nav-signup-btn"
              >
                <User size={16} />
                <span>Sign Up</span>
              </Button>
              {/* Mobile: Icon only */}
              <Button 
                size="icon"
                className="bg-[#1A4D2E] hover:bg-[#2D6A4F] text-white sm:hidden"
                data-testid="nav-signup-btn-mobile"
              >
                <User size={18} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </nav>

      {/* Hero Section - Mobile First */}
      <section className="relative min-h-[calc(100dvh-60px)] pt-6 pb-8 md:pt-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:grid lg:grid-cols-12 gap-6 md:gap-12 items-center">
            
            {/* Mobile Hero Image - Shows first on mobile */}
            <motion.div 
              className="w-full lg:hidden relative mb-4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3]">
                <img 
                  src="https://images.unsplash.com/photo-1589799266381-e828a874afb1?w=800&q=70" 
                  alt="Holistic wellness yoga practice"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A4D2E]/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <p className="font-serif text-xl font-bold mb-1">Holistic Health</p>
                  <p className="text-white/80 text-sm">Mind, Body & Spirit in harmony</p>
                </div>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div 
              className="lg:col-span-5 space-y-5 md:space-y-8 text-center lg:text-left"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-[#D2E823]/20 rounded-full">
                <Sparkles className="w-4 h-4 text-[#1A4D2E]" />
                <span className="text-xs md:text-sm font-medium text-[#1A4D2E]">Transform Your Life</span>
              </div>
              
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-[#1A4D2E] leading-tight tracking-tight">
                Your Journey to <span className="italic">Holistic</span> Wellness
              </h1>
              
              <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-lg mx-auto lg:mx-0">
                Embark on a transformative 3-month wellness journey with personalized diet plans 
                and lifestyle recommendations crafted just for you.
              </p>

              {/* CTA Buttons - Stacked on mobile, side by side on larger */}
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center lg:justify-start">
                <Button
                  onClick={() => navigate('/questionnaire')}
                  className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90 text-white rounded-full px-6 md:px-8 h-14 md:h-16 text-base md:text-lg font-medium shadow-lg active:scale-[0.98] touch-manipulation"
                  data-testid="start-journey-btn"
                >
                  Start Your Journey
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  className="border-2 border-[#1A4D2E]/20 text-[#1A4D2E] hover:border-[#1A4D2E] hover:bg-[#1A4D2E]/5 rounded-full px-6 md:px-8 h-14 md:h-16 text-base md:text-lg"
                  data-testid="learn-more-btn"
                >
                  Learn More
                </Button>
              </div>

              {/* Social Proof - Simplified on mobile */}
              <div className="flex items-center gap-4 md:gap-6 pt-2 md:pt-4 justify-center lg:justify-start">
                <div className="flex -space-x-2 md:-space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div 
                      key={i} 
                      className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-[#1A4D2E] to-[#D2E823] border-2 border-white"
                    />
                  ))}
                </div>
                <div className="text-left">
                  <p className="font-semibold text-[#1A4D2E] text-sm md:text-base">500+ Happy Participants</p>
                  <p className="text-xs md:text-sm text-gray-500">Transformed their lives</p>
                </div>
              </div>
            </motion.div>

            {/* Desktop Hero Image */}
            <motion.div 
              className="hidden lg:block lg:col-span-7 relative"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1589799266381-e828a874afb1?w=1200&q=80" 
                  alt="Holistic wellness yoga practice"
                  className="w-full h-[500px] lg:h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A4D2E]/60 to-transparent" />
                <div className="absolute bottom-8 left-8 right-8 text-white">
                  <p className="font-serif text-2xl font-bold mb-2">Holistic Health</p>
                  <p className="text-white/80">Mind, Body & Spirit in harmony</p>
                </div>
              </div>
              
              {/* Floating card - Desktop only */}
              <motion.div 
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl border border-[#E5E0D1]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D2E823] flex items-center justify-center">
                    <Leaf className="w-6 h-6 text-[#1A4D2E]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#1A4D2E]">100% Natural</p>
                    <p className="text-sm text-gray-500">Indian Ingredients</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section - Mobile optimized grid */}
      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <motion.div 
            className="text-center mb-8 md:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-3 md:mb-4">
              How It Works
            </h2>
            <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto px-4">
              Our comprehensive approach ensures you receive a personalized wellness plan 
              that fits your lifestyle and goals.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="bg-[#F9F8F6] p-4 md:p-8 rounded-2xl md:rounded-3xl border border-[#E5E0D1] hover:shadow-lg transition-shadow duration-300 group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-[#1A4D2E] text-white flex items-center justify-center mb-3 md:mb-6 group-hover:bg-[#D2E823] group-hover:text-[#1A4D2E] transition-colors duration-300">
                  {feature.icon}
                </div>
                <h3 className="font-serif text-sm md:text-xl font-bold text-[#1A4D2E] mb-1 md:mb-3 leading-tight">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-xs md:text-base leading-relaxed hidden sm:block">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Mobile optimized */}
      <section className="py-12 md:py-24 bg-[#1A4D2E]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6">
              Ready to Transform Your Life?
            </h2>
            <p className="text-white/80 text-sm md:text-lg mb-6 md:mb-8 max-w-2xl mx-auto">
              Take the first step towards a healthier, happier you. 
              Complete our wellness questionnaire and receive your personalized plan.
            </p>
            <Button
              onClick={() => navigate('/questionnaire')}
              className="bg-[#D2E823] hover:bg-[#D2E823]/90 text-[#1A4D2E] rounded-full px-8 md:px-10 h-14 md:h-16 text-base md:text-lg font-bold shadow-xl active:scale-[0.98] touch-manipulation"
              data-testid="cta-start-btn"
            >
              Begin Your Assessment
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer - Mobile optimized */}
      <footer className="bg-[#F9F8F6] py-8 md:py-12 border-t border-[#E5E0D1]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 md:gap-3">
              <img 
                src={FBM_LOGO_URL} 
                alt="Fit Bharat Mission" 
                className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover border-2 border-[#D2E823]"
              />
              <span className="font-serif text-base md:text-lg font-bold text-[#1A4D2E]">Fit Bharat Mission</span>
              <span className="text-gray-500 text-xs md:text-sm ml-1 md:ml-2 hidden sm:inline">| Health hai to Sab hai</span>
            </div>
            <div className="flex items-center gap-4 md:gap-6">
              <button
                onClick={() => navigate('/admin/login')}
                className="text-gray-500 hover:text-[#1A4D2E] text-sm transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                Login
              </button>
              <p className="text-gray-500 text-xs md:text-sm">
                © {new Date().getFullYear()} Fit Bharat Mission
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
